/* iter132 — Entegrasyonlar / API Bağlantı ve Senkronizasyon Merkezi.
 *
 * Central hub UI for managing external provider connections
 * (Paraşüt, BirFatura, Pastel Bulut, Trendyol, Hepsiburada).
 *
 * Design contract (spec Iter 132):
 *  - Credentials are NEVER shown in plaintext (server returns `credentials_masked`).
 *  - Sync capabilities follow DENY-BY-DEFAULT — a capability must be explicitly
 *    checked to be enabled per connection.
 *  - Test / Sync / Disable / History actions available per row.
 *  - Supports multiple connections per own_company.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Modal, Input, Select, Textarea, PageHeader, Badge } from "@/components/UI";
import {
  Plus, RefreshCw, Trash2, Edit3, History, ShieldCheck, ShieldOff,
  PlugZap, KeyRound, CheckCircle2, XCircle, Loader2, Activity, Building2,
} from "lucide-react";

const PROVIDER_LABEL = {
  PARASUT: "Paraşüt",
  BIRFATURA: "BirFatura (EFaturaV2)",
  PASTEL_BULUT: "Pastel Bulut / Sopyo",
  TRENDYOL: "Trendyol Pazaryeri",
  HEPSIBURADA: "Hepsiburada Pazaryeri",
};

const STATUS_STYLE = {
  connected: { l: "BAĞLI", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  error: { l: "HATA", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  pending_credentials: { l: "CREDENTIALS BEKLENİYOR", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  disabled: { l: "DEVRE DIŞI", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

export default function IntegrationsPage() {
  const [caps, setCaps] = useState({ capabilities: {}, providers: {}, own_companies: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [syncAllBusy, setSyncAllBusy] = useState(false);

  const loadCaps = useCallback(async () => {
    try {
      const c = await api.get("/integrations/capabilities");
      setCaps(c);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Yetkinlikler yüklenemedi");
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/connections");
      setRows(Array.isArray(r) ? r : []);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Bağlantılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCaps(); load(); }, [loadCaps, load]);

  const doTest = async (r) => {
    setBusyId(r.id + ":test");
    try {
      const res = await api.post(`/integrations/connections/${r.id}/test`);
      const d = res.diagnostics || {};
      const ch = res.checks || {};
      const fmt = (probe) => {
        if (!probe || Object.keys(probe).length === 0) return null;
        const st = probe.state || "?";
        const httpStr = probe.http_status ? ` HTTP ${probe.http_status}` : "";
        const ctStr = probe.content_type ? ` · ${probe.content_type}` : "";
        const cnt = probe.count != null ? ` · ${probe.count} kayıt` : "";
        if (st === "OK") return `✓ ${st}${httpStr}${cnt}`;
        return `✗ ${st}${httpStr}${ctStr} — ${probe.error || ""}`;
      };
      const parts = [];
      if (ch.authentication) parts.push(`Auth: ${ch.authentication === "OK" ? "✓ OK" : "✗ " + ch.authentication}`);
      const inc = fmt(d.list_incoming);
      const out = fmt(d.list_outgoing);
      if (inc) parts.push(`Gelen: ${inc}`);
      if (out) parts.push(`Giden: ${out}`);
      if (!inc && !out && res.checks) parts.push(Object.entries(ch).filter(([k]) => k !== "authentication").map(([k, v]) => `${k}: ${v}`).join(" · "));
      if (res.error) parts.push(res.error);
      setMsg(`[TEST] ${PROVIDER_LABEL[r.provider] || r.provider} · ${res.ok ? "✓ BAŞARILI" : "✗ HATA"} — ${parts.join(" · ") || "—"} (${res.duration_ms ?? "?"}ms)`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Test hatası"); }
    finally { setBusyId(null); }
  };

  const doSync = async (r, windowDays) => {
    setBusyId(r.id + ":sync");
    try {
      // iter141 — Optional windowDays widens the diagnostic date range
      const url = `/integrations/connections/${r.id}/sync${windowDays ? `?window_days=${windowDays}` : ""}`;
      const res = await api.post(url);
      const capSummary = Object.entries(res.capabilities || {}).map(([cap, v]) => {
        if (v.error) {
          const d = v.diag || {};
          const sw = (d.sweep || []).map(s =>
            `${s.systemType || "-"}:HTTP ${s.http_status ?? "?"}${s.response_meta && Object.keys(s.response_meta).length ? ` ${JSON.stringify(s.response_meta)}` : ""}`
          ).join("|");
          return `${cap}=ERR ${sw ? `[${sw}] ` : ""}${(v.error || "").slice(0, 80)}`;
        }
        const raw = v.raw_total ?? v.fetched ?? 0;
        const sw = (v.sweep || []).map(s => {
          const meta = s.response_meta && Object.keys(s.response_meta).length
            ? ` ${Object.entries(s.response_meta).map(([k,val]) => `${k}=${val}`).join(",")}`
            : "";
          return `${s.systemType || "-"}:${s.total_rows_this_system ?? 0}(HTTP ${s.http_status ?? "?"}${meta})`;
        }).join("|");
        return `${cap}=${v.new ?? 0} yeni / ${v.fetched ?? 0} normalize / ${raw} ham${sw ? ` [${sw}]` : ""}`;
      }).join(" · ") || "yetki yok";
      setMsg(`[SYNC iter141 ${res.window_days_used || 7}g] ${PROVIDER_LABEL[r.provider] || r.provider} · ${res.ok ? "✓" : "⚠"} ${capSummary} (${res.duration_ms ?? "?"}ms)`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Sync hatası"); }
    finally { setBusyId(null); }
  };

  const doSyncWide = async (r, days) => {
    if (!window.confirm(`${days} günlük tanılama senkronu çalıştırılsın mı? (Sadece diagnostic — kalıcı ayar değişmez)`)) return;
    return doSync(r, days);
  };

  const doDisable = async (r) => {
    if (!window.confirm(`"${r.name}" bağlantısı devre dışı bırakılsın? İş kayıtları (faturalar) korunur, harici providera hiçbir istek gönderilmez.`)) return;
    try {
      await api.del(`/integrations/connections/${r.id}`);
      setMsg(`Bağlantı devre dışı bırakıldı: ${r.name}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Hata"); }
  };

  const doSyncAll = async () => {
    if (!window.confirm("Tüm aktif bağlantılar için (yalnızca capability olarak seçilmiş) veri senkronize edilecek. Otomatik ödeme/çek/kasa aktarımı YAPILMAZ. Devam?")) return;
    setSyncAllBusy(true);
    try {
      const res = await api.post("/integrations/sync-all");
      setMsg(`[SYNC-ALL] ${res.count} bağlantı işlendi · ` + (res.results || []).map(x => `${x.provider}=${x.ok ? "✓" : "✗"}`).join(" · "));
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Toplu senkron hatası"); }
    finally { setSyncAllBusy(false); }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter(r => r.active).length;
    const errored = rows.filter(r => r.status === "error").length;
    const capsCount = rows.reduce((n, r) => n + Object.values(r.sync_profile || {}).filter(Boolean).length, 0);
    return { total, active, errored, capsCount };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="integrations-page">
      <PageHeader
        title="Entegrasyonlar"
        description="API bağlantıları ve senkronizasyon merkezi · Deny-by-default · Credentials şifrelenmiş"
        actions={
          <>
            <Btn variant="secondary" onClick={doSyncAll} disabled={syncAllBusy || rows.length === 0} data-testid="integ-sync-all">
              {syncAllBusy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Tümünü Senkronize Et
            </Btn>
            <Btn onClick={() => { setEditRow(null); setShowForm(true); }} data-testid="integ-add-btn">
              <Plus size={14}/> Yeni Bağlantı
            </Btn>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard label="Toplam Bağlantı" value={stats.total} icon={PlugZap} tone="indigo" testid="integ-stat-total"/>
        <StatCard label="Aktif" value={stats.active} icon={ShieldCheck} tone="emerald" testid="integ-stat-active"/>
        <StatCard label="Hatalı" value={stats.errored} icon={ShieldOff} tone="rose" testid="integ-stat-errored"/>
        <StatCard label="Aktif Yetkinlikler" value={stats.capsCount} icon={Activity} tone="violet" testid="integ-stat-caps"/>
      </div>

      {msg && (
        <div className="text-xs bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200 rounded p-2 flex items-start gap-2" data-testid="integ-msg">
          <Activity size={13} className="mt-0.5 shrink-0"/>
          <div className="flex-1">{msg}</div>
          <button onClick={() => setMsg("")} className="text-indigo-500 hover:text-indigo-700">×</button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="integ-table">
            <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Provider</th>
                <th className="text-left px-3 py-2">Ad</th>
                <th className="text-left px-3 py-2">Firma</th>
                <th className="text-left px-3 py-2">Durum</th>
                <th className="text-left px-3 py-2">Aktif Yetkinlikler</th>
                <th className="text-left px-3 py-2">Son Senkronizasyon</th>
                <th className="text-left px-3 py-2 w-[340px]">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-400"><Loader2 className="inline animate-spin" size={14}/> Yükleniyor…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400 dark:text-slate-500">
                  Henüz bağlantı yok. <button onClick={() => setShowForm(true)} className="text-indigo-600 hover:underline font-semibold" data-testid="integ-empty-add">Yeni Bağlantı Ekle</button>.
                </td></tr>
              )}
              {rows.map(r => {
                const st = STATUS_STYLE[r.active ? r.status : "disabled"] || STATUS_STYLE[r.status] || { l: r.status, cls: "bg-slate-100 text-slate-700 border-slate-300" };
                const activeCaps = Object.entries(r.sync_profile || {}).filter(([, v]) => v).map(([k]) => k);
                return (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-700/50 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`integ-row-${r.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-200">{PROVIDER_LABEL[r.provider] || r.provider}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 flex items-center gap-1"><Building2 size={11} className="text-slate-400"/> {r.own_company}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${st.cls}`} data-testid={`integ-status-${r.id}`}>{st.l}</span></td>
                    <td className="px-3 py-2">
                      {activeCaps.length === 0
                        ? <span className="text-[10px] text-rose-600 italic">yok (deny-by-default)</span>
                        : (
                          <div className="flex flex-wrap gap-1">
                            {activeCaps.map(c => (
                              <span key={c} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded" title={caps.capabilities?.[c] || c}>
                                {caps.capabilities?.[c] || c}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {r.last_sync_at ? new Date(r.last_sync_at).toLocaleString("tr-TR") : "—"}
                      {r.last_sync_error && <div className="text-[10px] text-rose-600">{r.last_sync_error}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => doTest(r)} disabled={busyId === r.id + ":test"}
                          className="text-[10px] px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 disabled:opacity-50 flex items-center gap-1" data-testid={`integ-test-${r.id}`}>
                          {busyId === r.id + ":test" ? <Loader2 size={10} className="animate-spin"/> : <CheckCircle2 size={10}/>} Test Et
                        </button>
                        <button onClick={() => doSync(r)} disabled={busyId === r.id + ":sync" || !r.active}
                          className="text-[10px] px-2 py-1 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border border-indigo-300 disabled:opacity-50 flex items-center gap-1" data-testid={`integ-sync-${r.id}`}>
                          {busyId === r.id + ":sync" ? <Loader2 size={10} className="animate-spin"/> : <RefreshCw size={10}/>} Senkronize
                        </button>
                        <button onClick={() => doSyncWide(r, 30)} disabled={busyId === r.id + ":sync" || !r.active}
                          className="text-[10px] px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 disabled:opacity-50 flex items-center gap-1"
                          title="30 günlük tanılama senkronu" data-testid={`integ-sync30-${r.id}`}>
                          <RefreshCw size={10}/> 30g
                        </button>
                        <button onClick={() => doSyncWide(r, 90)} disabled={busyId === r.id + ":sync" || !r.active}
                          className="text-[10px] px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 disabled:opacity-50 flex items-center gap-1"
                          title="90 günlük tanılama senkronu" data-testid={`integ-sync90-${r.id}`}>
                          <RefreshCw size={10}/> 90g
                        </button>
                        <button onClick={() => { setEditRow(r); setShowForm(true); }}
                          className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 flex items-center gap-1" data-testid={`integ-edit-${r.id}`}>
                          <Edit3 size={10}/> Düzenle
                        </button>
                        <button onClick={() => setHistoryFor(r)}
                          className="text-[10px] px-2 py-1 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 flex items-center gap-1" data-testid={`integ-history-${r.id}`}>
                          <History size={10}/> Geçmiş
                        </button>
                        <button onClick={() => doDisable(r)}
                          className="text-[10px] px-2 py-1 rounded bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300 flex items-center gap-1" data-testid={`integ-disable-${r.id}`}>
                          <Trash2 size={10}/> Devre Dışı
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-[11px] text-slate-500 dark:text-slate-400 italic space-y-1">
        <div>
          <b>Güvenlik notu:</b> API anahtarları sunucuda Fernet ile şifrelenerek saklanır ve frontend'e yalnız maskeli olarak döner. Senkronizasyon “deny-by-default” çalışır — bir yetkinlik açıkça işaretlenmediği sürece hiçbir veri çekilmez.
          Faturalar Fatura Havuzu'na alınır; otomatik Borç/Alacak veya ödeme kaydı OLUŞTURULMAZ.
        </div>
        <div>
          <b>Runtime ≠ Kapasite:</b> Test/Sync sırasında oluşan <code>AUTH_ERROR</code>, <code>PARSE_ERROR</code>, <code>ENDPOINT_ERROR</code>, <code>REDIRECT</code>, <code>NETWORK_ERROR</code> gibi HTTP-seviyeli hatalar yapılandırılmış yetkinlikleri KALDIRMAZ. Credentials'ı düzelttikten sonra Test Et'e tekrar tıklayarak devam edebilirsiniz.
        </div>
      </div>

      {showForm && (
        <ConnectionForm
          caps={caps}
          editRow={editRow}
          onClose={() => { setShowForm(false); setEditRow(null); }}
          onSaved={() => { setShowForm(false); setEditRow(null); load(); }}
        />
      )}
      {historyFor && <HistoryModal row={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "indigo", testid }) {
  const tones = {
    indigo: "from-indigo-500 to-violet-500",
    emerald: "from-emerald-500 to-teal-500",
    rose: "from-rose-500 to-red-500",
    violet: "from-violet-500 to-fuchsia-500",
  };
  return (
    <Card className="p-3 flex items-center gap-3" data-testid={testid}>
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tones[tone]} text-white flex items-center justify-center shrink-0`}>
        <Icon size={18}/>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
        <div className="text-xl font-bold text-slate-800 dark:text-slate-100">{value}</div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Connection form modal
// -----------------------------------------------------------------------------
function ConnectionForm({ caps, editRow, onClose, onSaved }) {
  const isEdit = Boolean(editRow);
  const initialProvider = editRow?.provider || "PARASUT";
  const [form, setForm] = useState({
    provider: initialProvider,
    own_company: editRow?.own_company || (caps.own_companies?.[0] || ""),
    name: editRow?.name || "",
    active: editRow?.active ?? true,
    // credentials — cleared on edit (spec §17: full replace ONLY when user re-enters)
    creds: {},
    sync_profile: editRow?.sync_profile || {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const providerCaps = caps.providers?.[form.provider] || [];

  const setCred = (k, v) => setForm(f => ({ ...f, creds: { ...f.creds, [k]: v } }));
  const setCap = (cap, on) => setForm(f => ({ ...f, sync_profile: { ...f.sync_profile, [cap]: on } }));

  useEffect(() => {
    // Reset dependent fields when provider changes (new connection only)
    if (!isEdit) {
      // iter135 — Pre-seed BirFatura to the official OutEBelgeV2 contract so
      // save() actually sends the correct auth_type without the operator
      // having to toggle a hidden dropdown.
      const seed = form.provider === "BIRFATURA"
        ? { auth_type: "birfatura_outebelgev2" }
        : {};
      setForm(f => ({ ...f, creds: seed, sync_profile: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.provider]);

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("Ad zorunlu"); return; }
    if (!form.own_company) { setErr("Firma zorunlu"); return; }
    const anyCreds = Object.values(form.creds).some(v => v !== "" && v !== undefined && v !== null);
    if (!isEdit && !anyCreds) { setErr("En az bir credential girmelisiniz"); return; }
    setBusy(true);
    try {
      const payload = {
        provider: form.provider,
        own_company: form.own_company,
        name: form.name,
        sync_profile: form.sync_profile,
        active: form.active,
      };
      // iter137 — Strip empty-string / undefined credential fields so backend
      // merge (partial update semantics) preserves existing encrypted values.
      // Only send credentials the operator explicitly typed.
      const nonEmptyCreds = Object.fromEntries(
        Object.entries(form.creds).filter(([, v]) =>
          v !== "" && v !== undefined && v !== null &&
          !(typeof v === "string" && v.startsWith("*") && v.match(/^\*+/))
        )
      );
      // Always carry auth_type forward on create so the server never falls
      // back to legacy defaults. On edit, only send if user changed it.
      if (!isEdit && form.provider === "BIRFATURA" && !nonEmptyCreds.auth_type) {
        nonEmptyCreds.auth_type = "birfatura_outebelgev2";
      }
      const meaningfulCredKeys = Object.keys(nonEmptyCreds).filter(k => k !== "auth_type");
      if (meaningfulCredKeys.length > 0 || (!isEdit && Object.keys(nonEmptyCreds).length > 0)) {
        payload.credentials = nonEmptyCreds;
      }
      if (isEdit) {
        await api.put(`/integrations/connections/${editRow.id}`, payload);
      } else {
        await api.post("/integrations/connections", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={isEdit ? `BAĞLANTIYI DÜZENLE — ${editRow.name}` : "YENİ BAĞLANTI"} size="lg">
      <div className="space-y-3" data-testid="integ-form-modal">
        {/* Provider selection is locked on edit */}
        <div className="grid grid-cols-2 gap-2">
          <Select label="Provider *" value={form.provider}
            disabled={isEdit}
            onChange={e => setForm({ ...form, provider: e.target.value })}
            data-testid="integ-form-provider">
            {Object.keys(caps.providers || {}).map(p => (
              <option key={p} value={p}>{PROVIDER_LABEL[p] || p}</option>
            ))}
          </Select>
          <Select label="Firma *" value={form.own_company}
            onChange={e => setForm({ ...form, own_company: e.target.value })}
            data-testid="integ-form-own-company">
            <option value="">— seçin —</option>
            {(caps.own_companies || []).map(oc => <option key={oc} value={oc}>{oc}</option>)}
          </Select>
        </div>
        <Input label="Bağlantı Adı *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder="ör. Meta Pharma - Paraşüt Prod" data-testid="integ-form-name"/>

        {/* Credential fields per provider */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50/60 dark:bg-slate-800/40">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-2">
            <KeyRound size={12}/> Kimlik Bilgileri
            {isEdit && (
              <span className="ml-2 text-[10px] normal-case font-normal text-slate-500">
                (boş bırakırsanız mevcut değerler korunur — mevcut maskeli görünüm aşağıda)
              </span>
            )}
          </div>
          {isEdit && editRow?.credentials_masked && (
            <div className="text-[10px] text-slate-500 mb-2 grid grid-cols-2 gap-1">
              {Object.entries(editRow.credentials_masked).map(([k, v]) => (
                <div key={k} data-testid={`integ-form-masked-${k}`}><span className="text-slate-400">{k}:</span> <span className="font-mono">{String(v ?? "—")}</span></div>
              ))}
            </div>
          )}
          <CredentialFields provider={form.provider} creds={form.creds} setCred={setCred} isEdit={isEdit}/>
        </div>

        {/* Capability toggles — DENY BY DEFAULT */}
        <div className="border border-indigo-200 dark:border-indigo-700 rounded-lg p-3 bg-indigo-50/40 dark:bg-indigo-900/20">
          <div className="text-xs font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 flex items-center gap-1 mb-2">
            <ShieldCheck size={12}/> Senkronizasyon Yetkinlikleri
            <span className="ml-2 text-[10px] normal-case font-normal text-slate-500">
              (yalnız işaretli olanlar çekilir · deny-by-default)
            </span>
          </div>
          {providerCaps.length === 0 && <div className="text-[11px] text-slate-500 italic">Bu provider için yetkinlik yok.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {providerCaps.map(cap => {
              const on = !!form.sync_profile[cap];
              return (
                <label key={cap} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 cursor-pointer">
                  <input type="checkbox" checked={on} onChange={e => setCap(cap, e.target.checked)} data-testid={`integ-form-cap-${cap}`}/>
                  <span className="flex-1">{caps.capabilities?.[cap] || cap}</span>
                  <code className="text-[9px] text-slate-400">{cap}</code>
                </label>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="integ-form-active"/>
          Bağlantı aktif (kapalı ise Senkronize Et çalışmaz)
        </label>

        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="integ-form-err">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="integ-form-save">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}
            {isEdit ? "Değişiklikleri Kaydet" : "Bağlantıyı Oluştur"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function CredentialFields({ provider, creds, setCred, isEdit }) {
  const help = (t) => <div className="text-[10px] text-slate-500 dark:text-slate-400 italic mb-1">{t}</div>;
  // iter135 — Hook must be at top level (React rules-of-hooks).
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  if (provider === "PARASUT") {
    return (
      <>
        {help("Paraşüt → Ayarlar → Uygulamalar → API Bilgileri ekranından alın. Company ID panel URL'inde görünür.")}
        <div className="grid grid-cols-2 gap-2">
          <Input label="Client ID" value={creds.client_id || ""} onChange={e => setCred("client_id", e.target.value)} data-testid="integ-cred-client_id"/>
          <Input label="Client Secret" type="password" value={creds.client_secret || ""} onChange={e => setCred("client_secret", e.target.value)} data-testid="integ-cred-client_secret"/>
          <Input label="Kullanıcı (email)" value={creds.username || ""} onChange={e => setCred("username", e.target.value)} data-testid="integ-cred-username"/>
          <Input label="Şifre" type="password" value={creds.password || ""} onChange={e => setCred("password", e.target.value)} data-testid="integ-cred-password"/>
          <Input label="Company ID" value={creds.company_id || ""} onChange={e => setCred("company_id", e.target.value)} data-testid="integ-cred-company_id"/>
        </div>
      </>
    );
  }
  if (provider === "BIRFATURA") {
    // iter135 — Simplified BirFatura form.
    // Default: Official OutEBelgeV2 contract (X-Api-Key + X-Secret-Key + X-Integration-Key).
    // Legacy custom-endpoint modes (bearer/basic/api_key) are only offered when
    // editing an existing legacy connection or when the operator explicitly
    // toggles the "Gelişmiş" switch. This removes irrelevant fields
    // (Client ID / Bearer Token / etc.) from the primary flow.
    const authType = creds.auth_type || "birfatura_outebelgev2";
    const isV2 = authType === "birfatura_outebelgev2";
    return (
      <>
        {help("BirFatura Resmi API — OutEBelgeV2 Listeleme (developers.birfatura.com). Anahtarları BirFatura paneli → Uygulamalar → API Bilgileri → 'Kendim Kullanacağım · EFaturaV2 Belge İşlemleri' ekranından alın.")}
        {isV2 ? (
          <div className="grid grid-cols-1 gap-2">
            <Input label="X-Api-Key *" type="password" value={creds.api_key || ""}
              onChange={e => setCred("api_key", e.target.value)}
              placeholder="BirFatura panelinden alınır"
              data-testid="integ-cred-api_key"/>
            <Input label="X-Secret-Key *" type="password" value={creds.secret_key || ""}
              onChange={e => setCred("secret_key", e.target.value)}
              placeholder="BirFatura panelinden alınır"
              data-testid="integ-cred-secret_key"/>
            <Input label="X-Integration-Key *" type="password" value={creds.integration_key || ""}
              onChange={e => setCred("integration_key", e.target.value)}
              placeholder="BirFatura panelinden alınır"
              data-testid="integ-cred-integration_key"/>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Bizim VKN'ler (virgülle)"
                value={creds.own_tax_ids_str || (creds.own_tax_ids || []).join(", ")}
                onChange={e => { setCred("own_tax_ids_str", e.target.value); setCred("own_tax_ids", e.target.value.split(",").map(s => s.trim()).filter(Boolean)); }}
                placeholder="1234567890, 9876543210"
                data-testid="integ-cred-own_tax_ids"/>
              <Input label="Base URL (opsiyonel)" value={creds.base_url || ""}
                onChange={e => setCred("base_url", e.target.value)}
                placeholder="https://uygulama.edonustur.com"
                data-testid="integ-cred-base_url"/>
            </div>
          </div>
        ) : (
          // Legacy edit — keep read/edit access to old configurable adapter but
          // clearly labelled as advanced.
          <div className="space-y-2 border border-amber-300 rounded p-2 bg-amber-50/50 dark:bg-amber-900/20">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
              LEGACY — Özel Endpoint ({authType})
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Base URL" value={creds.base_url || ""} onChange={e => setCred("base_url", e.target.value)}/>
              <Input label="Gelen Path" value={creds.list_incoming_path || ""} onChange={e => setCred("list_incoming_path", e.target.value)}/>
              <Input label="Giden Path" value={creds.list_outgoing_path || ""} onChange={e => setCred("list_outgoing_path", e.target.value)}/>
              {authType === "bearer" && <Input label="Bearer Token" type="password" value={creds.auth_token || ""} onChange={e => setCred("auth_token", e.target.value)} data-testid="integ-cred-auth_token"/>}
              {authType === "basic" && <>
                <Input label="Kullanıcı" value={creds.username || ""} onChange={e => setCred("username", e.target.value)}/>
                <Input label="Şifre" type="password" value={creds.password || ""} onChange={e => setCred("password", e.target.value)}/>
              </>}
              {authType === "api_key" && <>
                <Input label="Header Adı" value={creds.auth_header_name || "X-API-Key"} onChange={e => setCred("auth_header_name", e.target.value)}/>
                <Input label="API Key" type="password" value={creds.api_key || ""} onChange={e => setCred("api_key", e.target.value)}/>
              </>}
            </div>
            <button type="button" onClick={() => setCred("auth_type", "birfatura_outebelgev2")}
              className="text-[10px] text-indigo-700 hover:underline">
              Resmi OutEBelgeV2 kontratına geç →
            </button>
          </div>
        )}
        {/* Optional advanced toggle for new connections — hidden by default */}
        {!isEdit && isV2 && (
          <div className="text-right">
            <button type="button" onClick={() => setShowAdvanced(v => !v)}
              className="text-[10px] text-slate-400 hover:text-slate-600 hover:underline"
              data-testid="integ-cred-advanced-toggle">
              {showAdvanced ? "Gelişmiş modu kapat" : "Gelişmiş (özel endpoint)"}
            </button>
            {showAdvanced && (
              <div className="mt-2">
                <Select label="Auth Modu (özel endpoint için)" value={authType}
                  onChange={e => setCred("auth_type", e.target.value)}
                  data-testid="integ-cred-auth_type">
                  <option value="birfatura_outebelgev2">BirFatura OutEBelgeV2 (Resmi - Önerilen)</option>
                  <option value="bearer">Bearer Token (özel endpoint)</option>
                  <option value="basic">HTTP Basic (özel endpoint)</option>
                  <option value="api_key">API Key custom header (özel endpoint)</option>
                </Select>
              </div>
            )}
          </div>
        )}
      </>
    );
  }
  if (provider === "PASTEL_BULUT") {
    return (
      <>
        {help("Sopyo / Pastel Bulut için özel entegrasyon anahtarını Sopyo destek ekibinden temin edin.")}
        <Input label="API Key" type="password" value={creds.api_key || ""} onChange={e => setCred("api_key", e.target.value)} data-testid="integ-cred-api_key"/>
      </>
    );
  }
  if (provider === "TRENDYOL" || provider === "HEPSIBURADA") {
    return (
      <>
        {help(`${provider === "TRENDYOL" ? "Trendyol" : "Hepsiburada"} entegratör panelinden mağaza kimliği + API key/secret alın. (Adapter Faz A: yapılandırma-only)`)}
        <div className="grid grid-cols-2 gap-2">
          <Input label="Supplier / Merchant ID" value={creds.supplier_id || ""} onChange={e => setCred("supplier_id", e.target.value)} data-testid="integ-cred-supplier_id"/>
          <Input label="API Key" type="password" value={creds.api_key || ""} onChange={e => setCred("api_key", e.target.value)}/>
          <Input label="API Secret" type="password" value={creds.api_secret || ""} onChange={e => setCred("api_secret", e.target.value)}/>
        </div>
      </>
    );
  }
  return <div className="text-[11px] text-slate-500 italic">Bu provider için credential şeması tanımsız.</div>;
}

// -----------------------------------------------------------------------------
// History modal
// -----------------------------------------------------------------------------
function HistoryModal({ row, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/integrations/history?connection_id=${row.id}`);
        if (alive) setLogs(r?.rows || []);
      } catch { if (alive) setLogs([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [row.id]);
  return (
    <Modal open={true} onClose={onClose} title={`SENKRONİZASYON GEÇMİŞİ — ${row.name}`} size="xl">
      <div className="space-y-2" data-testid="integ-history-modal">
        <div className="text-[11px] text-slate-500">Son 200 kayıt · Test ve Sync işlemlerinin ayrıntısı (secret sızıntısı yoktur).</div>
        {loading && <div className="text-xs text-slate-400"><Loader2 className="inline animate-spin" size={12}/> Yükleniyor…</div>}
        {!loading && logs.length === 0 && <div className="text-xs text-slate-400 italic">Kayıt yok.</div>}
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 text-slate-600 dark:text-slate-300 uppercase">
              <tr>
                <th className="text-left px-2 py-2">Tarih</th>
                <th className="text-left px-2 py-2">Tür</th>
                <th className="text-left px-2 py-2">Durum</th>
                <th className="text-left px-2 py-2">Süre</th>
                <th className="text-left px-2 py-2">Yetkinlikler</th>
                <th className="text-left px-2 py-2">Detay</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.id || i} className="border-t border-slate-100 dark:border-slate-700/50 align-top" data-testid={`integ-history-row-${i}`}>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString("tr-TR")}</td>
                  <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${l.kind === "sync" ? "bg-indigo-100 text-indigo-800" : "bg-amber-100 text-amber-800"}`}>{l.kind}</span></td>
                  <td className="px-2 py-1.5">
                    {l.status === "success"
                      ? <span className="text-[10px] font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11}/> BAŞARILI</span>
                      : <span className="text-[10px] font-semibold text-rose-700 inline-flex items-center gap-1"><XCircle size={11}/> HATA</span>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{l.duration_ms ?? "—"} ms</td>
                  <td className="px-2 py-1.5 text-[10px]">
                    {Object.entries(l.sync_profile || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"}
                  </td>
                  <td className="px-2 py-1.5">
                    <pre className="text-[10px] whitespace-pre-wrap break-words bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded p-1 max-w-md max-h-32 overflow-auto">
{JSON.stringify(l.result || {}, null, 2)}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end pt-2 border-t border-slate-200 dark:border-slate-700">
          <Btn variant="ghost" onClick={onClose}>Kapat</Btn>
        </div>
      </div>
    </Modal>
  );
}
